import { useCallback, useRef, useState } from 'react'

export const useGeocoding = () => {
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)

  const initializeGeocoder = useCallback(() => {
    geocoderRef.current = new google.maps.Geocoder()
  }, [])

  const geocodeAddress = useCallback(
    (address: string): Promise<google.maps.GeocoderResult | null> => {
      return new Promise((resolve) => {
        if (!geocoderRef.current) {
          resolve(null)
          return
        }

        geocoderRef.current.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0])
          } else {
            resolve(null)
          }
        })
      })
    },
    []
  )

  const reverseGeocode = useCallback(
    (
      latLng: google.maps.LatLng
    ): Promise<google.maps.GeocoderResult | null> => {
      return new Promise((resolve) => {
        if (!geocoderRef.current) {
          resolve(null)
          return
        }

        geocoderRef.current.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0])
          } else {
            resolve(null)
          }
        })
      })
    },
    []
  )

  return { initializeGeocoder, geocodeAddress, reverseGeocode }
}

export const useMarker = (map: google.maps.Map | null) => {
  const [marker, setMarker] = useState<google.maps.Marker | null>(null)

  const updateMarker = useCallback(
    (position: google.maps.LatLngLiteral, animate: boolean = false) => {
      if (marker) {
        marker.setPosition(position)
      } else if (map) {
        const newMarker = new google.maps.Marker({
          position,
          map,
          draggable: true,
          animation: animate ? google.maps.Animation.DROP : undefined,
        })
        setMarker(newMarker)
      }
    },
    [map, marker]
  )

  const clearMarker = useCallback(() => {
    if (marker) {
      marker.setMap(null)
      setMarker(null)
    }
  }, [marker])

  return { marker, updateMarker, clearMarker }
}
